import { useCallback, useContext, useEffect, useRef, useState, useMemo } from "react";
import { ChatContext } from "./chatContext";
import { StreamChat } from "stream-chat";
import { useAuthContext } from "src/auth/hooks";
import { enqueueSnackbar } from 'notistack';
// import { useTranslation } from "react-i18next";
import { useGetChatCredentialsQuery } from "../../services/chatAPI.js"; 
import { decrypt } from "src/utils/custom-function";

const errorLocales = "errors.chatService";

export function ChatProvider({children}){
    //#region VARIABLES
    // const {t} = useTranslation();

    const { user } = useAuthContext();

    const customUserDetails = useMemo(() => {
        if (!user) return null;
        try {
            const decrypted = decrypt(user);
            return decrypted ? JSON.parse(decrypted) : null;
        } catch (error) {
            console.error("Failed to decrypt user details in ChatProvider:", error);
            return null;
        }
    }, [user]);

    const [inConnectionEstablishmentState, setInConnectionEstablishmentState] = useState(false);

    const [client, setClient] = useState(null);
    const [clientError, setClientError] = useState(null);
    const [activeChannel, setActiveChannel] = useState(null);

    const [chatID, setChatID] = useState(null);
    //#endregion

    //#region CONNECTION
    const {
        isFetching: isFetchingChatCredentials,
        isSuccess: isSuccessChatCredentials,
        data: dataChatCredentials,
        isError: isErrorChatCredentials,
        error: errorChatCredentials,
        refetch: reTryChatConnectionEstablishment
    } = useGetChatCredentialsQuery(undefined, {
        skip: customUserDetails === null || client !== null,
        refetchOnMountOrArgChange: true
    });

    // Reset Chat State and Disconnect Client on Logout
    useEffect(() => {
        if (customUserDetails === null) {
            console.log("***")
            if (client) {
                client.disconnectUser()
                    .then(() => console.log("Stream client disconnected successfully"))
                    .catch((err) => console.error("Error disconnecting Stream client:", err));
            }
            setClient(null);
            setChatID(null);
            setActiveChannel(null);
            setInConnectionEstablishmentState(false);
        }
    }, [customUserDetails, client]);

    useEffect(() => {
        if(isSuccessChatCredentials && dataChatCredentials){
            setInConnectionEstablishmentState(true);

            console.log(dataChatCredentials);

            setChatID(dataChatCredentials.data?.streamUserId);
            establishConnection(dataChatCredentials.data);
        }
        else if(isErrorChatCredentials && isErrorChatCredentials){
            console.error("Error connecting to Stream Chat:", isErrorChatCredentials);

            setClientError(isErrorChatCredentials);
        }
    }, [isSuccessChatCredentials, dataChatCredentials, isErrorChatCredentials, isErrorChatCredentials]);

    const establishConnection = useCallback(async (response) => {
        if(client) {
            setInConnectionEstablishmentState(false);
            return;
        }

        const user_id = response?.streamUserId;

        console.log("User Chat Id: " + user_id);
        console.log("User Details: ", customUserDetails);

        if(!user_id){
            setClientError('Chat user not found');
            return;
        }

        console.log("API Key: ", process.env.REACT_APP_STREAM_API_KEY);

        const chatClient = StreamChat.getInstance(process.env.REACT_APP_STREAM_API_KEY, {timeout: 6000});

        const userDetails = {
            id: user_id,
            name:
                customUserDetails?.firstName || customUserDetails?.lastName
                ? `${customUserDetails?.firstName || ''} ${customUserDetails?.lastName || ''}`.trim()
                : customUserDetails?.email || 'Admin',
            image: customUserDetails?.profilePicture?.downloadUrl || (typeof customUserDetails?.profilePicture === 'string' ? customUserDetails?.profilePicture : ''),
        };

        console.log("GetStream chat connection establishment user details:", userDetails);
        
        // Change this if you're using the token from the server
        const isRequiredDevToken = false;

        let token;

        if(isRequiredDevToken && process.env.REACT_APP_ENV === "Staging"){
            token = chatClient.devToken(user_id);
            console.log("GetStream chat connection establishing as developer mode");
        }
        else{
            // User token from the server
            token = response?.token;
        }

        console.log("GetStream chat connection establishment token:", token);

        try {
            await chatClient.connectUser(userDetails, token, {presence: true});

            setClientError(null);

            setClient(chatClient);

            console.log("Stream Chat connected successfully.");
        } catch (error) {
            setClientError(`Failed to establish a connection: ${error?.message}`);

            setClient(null);

            console.error("Error connecting to Stream Chat:", error);
        } finally {
            setInConnectionEstablishmentState(false);
        }
    }, [customUserDetails, client]);

    const isStreamInConnectionState = () => isFetchingChatCredentials || inConnectionEstablishmentState;

    const isStreamChatFailed = () => clientError;

    const isStreamChatConnected = () => client;

    const getUserChatID = () => chatID;
    //#endregion

    //#region NEW CHAT SETUP
    const connectToAChatChannel = useCallback(async(channelId, messageReceiverID) => {
        if(!client || !chatID){
            console.log("Failed to establish a connection with");

            return {
                success: false,
                data: "Failed to establish a connection with"
            };
        }

        try{
            console.log("Sender chat id: " + chatID);
            console.log("Receiver chat id: " + messageReceiverID);

            const channel = client.channel("messaging", channelId, {
                members: [chatID, messageReceiverID],
                name: channelId
            });
            console.log("CHANNEL ID:", channelId);
            console.log("ADMIN STREAM ID:", chatID);
            console.log("RECEIVER STREAM ID:", messageReceiverID);
            console.log(channelId)

            // fetch the channel state, subscribe to future updates
            const state = await channel.watch({
                presence: true // Listen for contact person online/offline presence status
            });
            console.log("CHANNEL MEMBERS", state.members);

            return {
                success: true,
                data: {
                    channel: channel,
                    state: state
                }
            };
        }catch (error) {
            console.error(`Error connecting to Stream Channel with ${messageReceiverID}: `, error);

            return {
                success: false,
                data: `Failed to establish a connection with: ${error?.message}`
            };
        }
    }, [client, chatID]);
    //#endregion

    //#region SEND MESSAGE
    const sendTextMessage = useCallback(async(msg, channel) => {
        if(!client || !chatID){
            enqueueSnackbar("Message failed to send", {variant: 'error'});
            return null;
        }

        try{
            const response = await channel.sendMessage(
                {
                    text: msg,
                    priority: "high"
                }
            );

            return response;
        }catch (error) {
            console.error("Error send message: ", error);

            enqueueSnackbar(
                `"Message failed to send": ${error?.message}`, 
                { variant: 'error', autoHideDuration: 5000 }
            );

            return null;
        }
    }, [client, chatID]);

    const sendAttachmentWithMessage = useCallback(async(msg, attachments, channel) => {
        if(!client || !chatID){
            enqueueSnackbar("Attachment failed to send", {variant: 'error'});
            return null;
        }

        // Attachment sample
        // [
        //     {
        //         type: "image",
        //         asset_url: "https://bit.ly/2K74TaG",
        //         thumb_url: "https://bit.ly/2Uumxti"
        //     }
        // ]

        try{
            const response = await channel.sendMessage(
                {
                    text: msg,
                    attachments: attachments,
                    priority: "high"
                }
            );

            return response;
        }catch (error) {
            console.error("Error send attachment:", error);

            enqueueSnackbar(
                `Attachment failed to send: ${error}`,
                { variant: 'error', autoHideDuration: 5000 }
            );

            return null;
        }
    }, [client, chatID]);
    //#endregion

    //#region RECEIVE MESSAGE
    const querySupportChannels = useCallback(async() => {
        if(!client){
            return {
                success: false,
                data: "Failed to query channels"
            };
        }

        try{
            const filters = { 
                type: 'messaging', 
                category: 'ADMIN_SUPPORT' 
            };
            const sort = [{ last_message_at: -1 }]; 

            const channels = await client.queryChannels(filters, sort, {
                watch: true,
                state: true,
            });

            return {
                success: true,
                data: channels
            };
        }catch (error) {
            console.error("Error querying support channels:", error);
            return {
                success: false,
                data: `Failed to query channels: ${error?.message}`
            };
        }
    }, [client]);

    const getLastMessage = useCallback(async(channelID) => {
        if(!client){
            return {
                success: false,
                data: "Failed to receive the previous messages"
            };
        }

        try{
            const filter = {cid: `messaging:${channelID}`};
            const sort = [{ last_message_at: -1 }];
            const options = { 
                limit: 1,
                message_limit: 1
            };

            const response = await client.queryChannels(filter, sort, options);

            if (response && response.length > 0) {
                const channel = response[0];

                const messages = channel?.state?.messages;
                const latestMsg = messages?.length > 0 ? messages[messages.length - 1] : null;

                return {
                    success: true,
                    data: {
                        message: latestMsg,
                        unreadCount: channel?.state?.unreadCount
                    }
                };
            }

            // Empty message
            return { 
                success: true, 
                data: null
            };
        }catch (error) {
            console.error("Error failed to receive the last messages:", error);

            return {
                success: false,
                data: `Failed to receive the previous messages: ${error?.message}`
            };
        }
    }, [client]);

    //chatlist
    const getSupportChannelsWithPagination = useCallback(
        async ({
            limit = 10,
            offset = 0,
            selectedUserCategory = "CUSTOMER",
            searchKey = ""
        }) => {
            if (!client) {
                return {
                    success: false,
                    data: [],
                    hasMore: false,
                    categoryMap: {},
                    total: 0,
                    error: "Stream client is not connected"
                };
            }

            try {
                const filters = {
                    type: "messaging",
                    category: "ADMIN_SUPPORT"
                };

                const sort = [
                    { last_message_at: -1 }
                ];

                const channels = await client.queryChannels(
                    filters,
                    sort,
                    {
                        watch: true,
                        state: true,
                        limit,
                        offset,
                        message_limit: 1
                    }
                );

                const categoryMap = {};

                const normalizeUserType = (userType) => {
                    if (userType === "END_USER") return "CUSTOMER";
                    if (userType === "CUSTOMER") return "CUSTOMER";
                    if (userType === "VENDOR") return "VENDOR";

                    return "CUSTOMER";
                };

                console.log("channels",channels);
                const mappedChannels = channels
                    .filter((channel) => {
                        const userType = normalizeUserType(channel.data?.userType);

                        categoryMap[channel.id] = userType;

                        if (userType !== selectedUserCategory) return false;

                        if (searchKey) {
                            const creatorName = (
                                channel.data?.created_by?.name || ""
                            ).toLowerCase();

                            if (!creatorName.includes(searchKey.toLowerCase())) {
                                return false;
                            }
                        }

                        return true;
                    })
                    .map((channel) => {
                        const creator = channel.data?.created_by;
                        const messages = channel.state?.messages || [];
                        const latestMsg = messages[messages.length - 1] || null;

                        return {
                            contactChannelId: channel.id,
                            contactStreamUserId: creator?.id || "",
                            contactName: creator?.name || "Unknown User",
                            contactProfileImage: creator?.image || "",
                            lastMessageAt: channel.data?.last_message_at || null,

                            lastMessageText: latestMsg?.text || (
                                latestMsg?.attachments?.length ? "Sent an attachment" : "No messages yet"
                            ),
                            lastMessageTime: latestMsg?.created_at || channel.data?.last_message_at || null,
                            unreadCount: channel?.state?.unreadCount || 0,
                        };
                    });

                return {
                    success: true,
                    data: mappedChannels,
                    hasMore: channels.length === limit,
                    categoryMap,
                    total: mappedChannels.length,
                    rawChannels: channels
                };
            } catch (error) {
                console.error("Error loading support channels:", error);

                return {
                    success: false,
                    data: [],
                    hasMore: false,
                    categoryMap: {},
                    total: 0,
                    error: error?.message || "Failed to load conversations"
                };
            }
        },
        [client]
    );

    //chat list getby id
    const getSupportChannelById = useCallback(async (channelId) => {
        if (!client || !channelId) {
            return {
                success: false,
                data: null
            };
        }

        try {
            const channels = await client.queryChannels(
                {
                    cid: `messaging:${channelId}`,
                    category: "ADMIN_SUPPORT"
                },
                [{ last_message_at: -1 }],
                {
                    watch: true,
                    state: true,
                    message_limit: 1
                }
            );

            return {
                success: true,
                data: channels?.[0] || null
            };
        } catch (error) {
            return {
                success: false,
                data: error?.message
            };
        }
    }, [client]);

    const listenToLastMessage = useCallback((channelID, callback) => {
        if(!client || !channelID) return null;

        try{
            const listener = client.on('message.new', (event) => {
                const incomingChannelCid = event.channel?.cid || event.cid;
                const incomingChannelId = incomingChannelCid?.split(':')[1];

                if(incomingChannelId === channelID && event.message){
                    callback(event.message);
                }
            });

            return listener;
        }catch (error) {
            console.error("Error failed to listen to the last messages:", error);
            return null;
        }
    }, [client]);

    const getPreviousMessagesWithPagination = useCallback(async(channel, size, oldestMessageID) => {
        if(!client){
            return {
                success: false,
                data: "Failed to receive the previous messages"
            };
        }

        try{
            const options = { limit: size };
            if (oldestMessageID) options.id_lt = oldestMessageID;

            const response = await channel.query({
                messages: options,
                watch: true
            });

            const messages = response?.messages || [];

            return {
                success: true,
                data: messages
            };
        }catch (error) {
            console.error("Error failed to receive the previous messages:", error);

            return {
                success: false,
                data: `Failed to receive the previous messages: ${error?.message}`
            };
        }
    }, [client]);

    const listenForNewMessages = useCallback((channel, setMessages) => {
        try{
            const listener = channel.on('message.new', (event) => {
                if (event.message) {
                    setMessages((prevMessages) => {
                        const messageExists = prevMessages.some(msg => msg.id === event.message.id);

                        if (messageExists) return prevMessages;

                        return [...prevMessages, event.message];
                    });
                }
            });

            return listener;
        }catch (error) {
            console.error("Error failed to receive the previous messages:", error);
            return null;
        }
    }, []);
    //#endregion

    //#region UNREAD MESSAGES
    const listenForGlobalUnreadCounts = useCallback((onUnreadUpdate) => {
        if (!client) return null;

        console.log("Unread message count listener...");

        const listener = client.on('message.new', (event) => {
            if (event.message?.user?.id === chatID) return;

            const channelCid = event.channel?.cid || event.cid;

            if (channelCid) {
                const channelID = channelCid.split(':')[1];
                console.log(channelID);
            
                const targetChannel = client.channel('messaging', channelID);
                console.log(targetChannel);

                const liveCount = targetChannel.countUnread(); 
                console.log(liveCount);
                
                onUnreadUpdate(channelID, liveCount);
            }
        });

        return listener;
    }, [client, chatID]);

    const markAllAsRead = useCallback(async(channel, clearUnreadStatus) => {
        try{
            await channel.markRead();
            const filters = {
                    type: "messaging",
                    category: "ADMIN_SUPPORT"
                };

                const sort = [
                    { last_message_at: -1 }
                ];

                const channels = await client.queryChannels(
                    filters,
                    sort,
                    {
                        watch: true,
                        state: true,
                        limit: 10,
                        offset: 0,
                        message_limit: 1
                    }
                );
                console.log("Markchannels",channels)
            clearUnreadStatus();
        }
        catch(err){
            console.log(err);
        }
    }, [client]);
    //#endregion

    //#region ONLINE/OFFLINE
    const listenForContactPersonOnlineStatus = useCallback((chatPartnerId, callback) => {
        if(!client || !chatPartnerId) return null;

        try{
            const listener = client.on("user.presence.changed", (event) => {
                console.log(event);

                if (event?.user?.id === chatPartnerId) {
                    console.log("Target user presence changed event:", event);
                    callback(event.user);
                }
            });

            return listener;
        }catch(err){
            console.error("Presence listener error:", err);
            return null;
        }
    }, [client]);
    //#endregion

    const value = {
        isStreamInConnectionState,
        isStreamChatConnected,
        isStreamChatFailed,
        reTryChatConnectionEstablishment,

        connectToAChatChannel,

        getUserChatID,

        sendTextMessage,
        sendAttachmentWithMessage,

        getLastMessage,
        listenToLastMessage,
        getPreviousMessagesWithPagination,
        listenForNewMessages,

        listenForGlobalUnreadCounts,
        markAllAsRead,

        listenForContactPersonOnlineStatus,
        querySupportChannels,
        getSupportChannelsWithPagination,
        getSupportChannelById,
        client
    }

    return (
        <ChatContext.Provider value={value}>
            {children}
        </ChatContext.Provider>
    );
}

export const useChat = () => {
    return useContext(ChatContext);
};